import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@design-system/Button/Button";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { ArrowCornerIcon, TriangleSmallIcon } from "@icons";
import type { SelectedPreviewEntity } from "./applyCoaTemplatePreviewEntries";
import { openEntityChartOfAccounts } from "../appliedEntities/openEntityChartOfAccounts";
import { ApplyCoaPreviewEntityList } from "./ApplyCoaPreviewEntityList";
import styles from "./ApplyCoaTemplatePreviewStep.module.scss";
import { Tooltip } from "@design-system/Tooltip/Tooltip";

interface Props {
    entries: SelectedPreviewEntity[];
    activeEntityId: number | null;
    selectedEntityIds: ReadonlySet<number>;
    disabledEntityIds: ReadonlySet<number>;
    // Optional per-entity explanation for disabled controls. The live apply flow
    // has none; the history drawer supplies undo-specific reasons.
    disabledReasonsByEntityId?: ReadonlyMap<number, string>;
    appliedEntityIds?: ReadonlySet<number>;
    applyingEntityId?: number | null;
    revertedEntityIds?: ReadonlySet<number>;
    selectionMode: "apply" | "undo" | "readOnly";
    preview: ReactNode;
    footer: ReactNode;
    title?: string;
    emptyStateHeader?: string;
    onActiveEntityChange: (entityId: number | null) => void;
    onSelectedEntityIdsChange: (entityIds: ReadonlySet<number>) => void;
}

const EMPTY_IDS: ReadonlySet<number> = new Set();
const EMPTY_REASONS: ReadonlyMap<number, string> = new Map();

export const ApplyCoaTemplatePreviewShell = ({
    entries,
    activeEntityId,
    selectedEntityIds,
    disabledEntityIds,
    disabledReasonsByEntityId = EMPTY_REASONS,
    appliedEntityIds = EMPTY_IDS,
    applyingEntityId = null,
    revertedEntityIds = EMPTY_IDS,
    selectionMode,
    preview,
    footer,
    title = "Preview chart of accounts",
    emptyStateHeader = "No accessible entities",
    onActiveEntityChange,
    onSelectedEntityIdsChange,
}: Props) => {
    const [search, setSearch] = useState<string | undefined>();
    const [collapsedClients, setCollapsedClients] = useState<Set<string>>(
        new Set(),
    );
    const filteredEntries = useMemo(() => {
        const term = search?.trim().toLowerCase();
        return term
            ? entries.filter((entry) => entry.name.toLowerCase().includes(term))
            : entries;
    }, [entries, search]);
    const groupedEntries = useMemo(() => {
        const groups = new Map<string, SelectedPreviewEntity[]>();
        for (const entry of filteredEntries) {
            const group = groups.get(entry.clientLabel) ?? [];
            group.push(entry);
            groups.set(entry.clientLabel, group);
        }
        return [...groups.entries()];
    }, [filteredEntries]);
    const activeIndex = filteredEntries.findIndex(
        (entry) => entry.entityId === activeEntityId,
    );
    const activeEntry =
        activeIndex >= 0 ? filteredEntries[activeIndex] : undefined;

    useEffect(() => {
        if (filteredEntries.length === 0 && activeEntityId !== null) {
            onActiveEntityChange(null);
        } else if (filteredEntries.length > 0 && activeIndex === -1) {
            onActiveEntityChange(filteredEntries[0].entityId);
        }
    }, [activeEntityId, activeIndex, filteredEntries, onActiveEntityChange]);

    const toggleClientCollapsed = useCallback((clientLabel: string) => {
        setCollapsedClients((previous) => {
            const next = new Set(previous);
            if (next.has(clientLabel)) {
                next.delete(clientLabel);
            } else {
                next.add(clientLabel);
            }
            return next;
        });
    }, []);

    const toggleEntitySelection = useCallback(
        (entityId: number) => {
            if (disabledEntityIds.has(entityId)) {
                return;
            }
            const next = new Set(selectedEntityIds);
            if (next.has(entityId)) {
                next.delete(entityId);
            } else {
                next.add(entityId);
            }
            onSelectedEntityIdsChange(next);
        },
        [disabledEntityIds, onSelectedEntityIdsChange, selectedEntityIds],
    );

    const toggleClientSelection = useCallback(
        (clientEntries: SelectedPreviewEntity[]) => {
            const next = new Set(selectedEntityIds);
            const isAllSelected = clientEntries.every((entry) =>
                next.has(entry.entityId),
            );
            for (const entry of clientEntries) {
                if (isAllSelected) {
                    next.delete(entry.entityId);
                } else {
                    next.add(entry.entityId);
                }
            }
            onSelectedEntityIdsChange(next);
        },
        [onSelectedEntityIdsChange, selectedEntityIds],
    );

    if (entries.length === 0) {
        return <EmptyState header={emptyStateHeader} />;
    }

    return (
        <div className={styles.container}>
            <ApplyCoaPreviewEntityList
                search={search}
                onSearchChange={setSearch}
                groupedEntries={groupedEntries}
                collapsedClients={collapsedClients}
                selectedEntityIds={selectedEntityIds}
                activeEntityId={activeEntityId}
                appliedEntityIds={appliedEntityIds}
                applyingEntityId={applyingEntityId}
                disabledEntityIds={disabledEntityIds}
                disabledReasonsByEntityId={disabledReasonsByEntityId}
                revertedEntityIds={revertedEntityIds}
                selectionMode={selectionMode}
                onToggleClientCollapsed={toggleClientCollapsed}
                onToggleClientSelection={toggleClientSelection}
                onSelectEntity={onActiveEntityChange}
                onToggleEntitySelection={toggleEntitySelection}
            />
            <div className={styles.previewContainer}>
                <div className={styles.previewHeaderRow}>
                    <h3 className={styles.previewHeaderTitle}>{title}</h3>
                    <div className={styles.previewHeaderRight}>
                        {filteredEntries.length > 1 && (
                            <>
                                <span className={styles.indexContainer}>
                                    <span className={styles.currentIndex}>
                                        {activeIndex + 1}
                                    </span>
                                    <span className={styles.totalCount}>
                                        {" "}
                                        / {filteredEntries.length}
                                    </span>
                                </span>
                                <Tooltip content="Previous entity">
                                    <Button
                                        variant="secondary"
                                        size="md"
                                        iconOnly
                                        aria-label="Previous entity"
                                        onClick={() =>
                                            activeIndex > 0 &&
                                            onActiveEntityChange(
                                                filteredEntries[activeIndex - 1]
                                                    .entityId,
                                            )
                                        }
                                        disabled={activeIndex <= 0}
                                    >
                                        <TriangleSmallIcon className="icon-rotate-180" />
                                    </Button>
                                </Tooltip>
                                <Tooltip content="Next entity">
                                    <Button
                                        variant="secondary"
                                        size="md"
                                        iconOnly
                                        aria-label="Next entity"
                                        onClick={() =>
                                            activeIndex >= 0 &&
                                            activeIndex <
                                                filteredEntries.length - 1 &&
                                            onActiveEntityChange(
                                                filteredEntries[activeIndex + 1]
                                                    .entityId,
                                            )
                                        }
                                        disabled={
                                            activeIndex >=
                                            filteredEntries.length - 1
                                        }
                                    >
                                        <TriangleSmallIcon />
                                    </Button>
                                </Tooltip>
                                <div className={styles.divider} />
                            </>
                        )}
                        <Button
                            variant="primary"
                            size="md"
                            rightIcon={
                                <ArrowCornerIcon className={styles.openIcon} />
                            }
                            onClick={() =>
                                activeEntry &&
                                openEntityChartOfAccounts({
                                    workspaceId: activeEntry.workspaceId,
                                    entityId: activeEntry.entityId,
                                })
                            }
                            disabled={!activeEntry}
                        >
                            Open client
                        </Button>
                    </div>
                </div>
                <div className={styles.previewContentContainer}>
                    <div className={styles.previewContent}>{preview}</div>
                    <div className={styles.previewFooter}>{footer}</div>
                </div>
            </div>
        </div>
    );
};
